import { useDataPart } from '@ai-sdk-tools/store';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { UIMessageWithMetadata } from '../../../lib/types';
import {
  type CoTStep,
  type ReasoningState,
  type ToolInvocationState,
  getCoTStepStatus,
  getField,
  getToolLabel,
  isFinalAnswerTextPart,
} from '../lib/chain-of-thought-utils';

interface ChainOfThoughtMeta {
  steps: CoTStep[];
  hasTextPart: boolean;
}

interface ChainOfThoughtStepsResult {
  chainOfThoughtMeta: ChainOfThoughtMeta;
  isWorking: boolean;
  stepProgressMap: Record<string, string>;
}

const EMPTY_META: ChainOfThoughtMeta = { steps: [], hasTextPart: false };
const EMPTY_PROGRESS: Record<string, string> = {};

export function useChainOfThoughtSteps(
  message: UIMessageWithMetadata,
  messageIsWorking: boolean,
  enabled: boolean,
): ChainOfThoughtStepsResult {
  const [progressData] = useDataPart<{ message?: string }>('progress');
  const parts = message.parts;

  const chainOfThoughtMeta = useMemo(() => {
    if (!parts) return EMPTY_META;

    const hasTextPart = parts.some((part) => part.type === 'text');

    if (!enabled) return { ...EMPTY_META, hasTextPart };

    const steps = parts
      .map((part, partIndex): CoTStep | null => {
        if (part.type === 'reasoning') {
          if (part.text) {
            const title = part.text.match(/^\*\*(.+?)\*\*/)?.[1];
            return { type: 'reasoning', text: part.text, title, state: part.state as ReasoningState, partIndex };
          }
          if (part.state === 'streaming') {
            return { type: 'reasoning', text: '', title: 'Thinking...', state: 'streaming', partIndex };
          }
        } else if (part.type.startsWith('tool-')) {
          const toolName = part.type.replace('tool-', '');
          const toolState = getField<ToolInvocationState>(part, 'state');
          const toolInput = getField<unknown>(part, 'input');
          const label = getToolLabel(toolName, toolInput);
          const toolOutput = toolState === 'output-available' ? getField<unknown>(part, 'output') : undefined;
          return { type: 'tool', toolName, label, state: toolState, input: toolInput, output: toolOutput, partIndex };
        }
        return null;
      })
      .filter((step): step is CoTStep => step !== null);

    const hasActiveStep = steps.some((step) => getCoTStepStatus(step) === 'active');
    const hasFinalAnswerTextPart = parts.some(isFinalAnswerTextPart);
    const workingReasoningStep: CoTStep = {
      type: 'reasoning',
      text: '',
      title: 'Thinking...',
      state: 'streaming',
      partIndex: parts.length,
    };
    const stepsWithWorkingState =
      messageIsWorking && !hasActiveStep && !hasFinalAnswerTextPart ? [...steps, workingReasoningStep] : steps;
    const indexedSteps = stepsWithWorkingState.map((step, stepIndex) => ({ ...step, stepIndex }));

    return {
      steps: indexedSteps,
      hasTextPart,
    };
  }, [parts, enabled, messageIsWorking]);

  const isWorking = enabled && messageIsWorking;

  // Snapshot progress messages per tool step so they don't leak across tool calls.
  // Only updates when progressData actually changes (not when steps change).
  const [stepProgressMap, setStepProgressMap] = useState<Record<string, string>>({});
  const lastProgressDataRef = useRef(progressData);

  useEffect(() => {
    if (!enabled) return;
    if (progressData === lastProgressDataRef.current) return;
    lastProgressDataRef.current = progressData;

    if (!progressData?.message) return;

    const { steps } = chainOfThoughtMeta;
    const reversedEntries = steps.map((step, i) => ({ step, i })).reverse();
    const active = reversedEntries.find(({ step }) => step.type === 'tool' && getCoTStepStatus(step) === 'active');

    if (active?.step.type === 'tool') {
      const key = `tool-${active.step.toolName}-${active.i}`;
      setStepProgressMap((prev) =>
        prev[key] === progressData.message ? prev : { ...prev, [key]: progressData.message! },
      );
    }
  }, [enabled, progressData, chainOfThoughtMeta]);

  return { chainOfThoughtMeta, isWorking, stepProgressMap: enabled ? stepProgressMap : EMPTY_PROGRESS };
}
